import type {
    EFaxListRow,
    FaxContact,
    FaxContactVo,
    FaxMessageVo,
    TFaxContactListPage,
    TFaxPagedMessages,
} from 'types/eFax';

/** Compare fax strings allowing +1 / formatting differences (e.g. +1555… vs 1555…). */
export function faxNumbersLooselyEqual(a: string, b: string): boolean {
    const da = (a || '').replace(/\D/g, '');
    const db = (b || '').replace(/\D/g, '');
    if (!da || !db) return false;
    if (da === db) return true;
    const tail = (d: string) => d.slice(-10);
    return tail(da) === tail(db);
}

export function findFaxContactByNumber(contacts: FaxContact[], fax: string): FaxContact | undefined {
    return contacts.find((c) => faxNumbersLooselyEqual(c.faxNumber, fax));
}

type FaxScheduleEnvelope = { code?: number; data?: unknown; message?: string };

export function isFaxScheduleSuccess(body: unknown): body is FaxScheduleEnvelope {
    if (!body || typeof body !== 'object') return false;
    const code = (body as FaxScheduleEnvelope).code;
    return code === 0 || code === 200;
}

/**
 * Format a raw fax/phone number to US display format: +1 (area) mid-last
 * e.g. "+15551111003" → "+1 (555) 111-1003"
 * Falls back to original string if it can't be parsed.
 */
export function formatFaxNumber(raw: string | null | undefined): string {
    if (!raw) return '';
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
        return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return raw;
}

/**
 * Fax number for POST /fax/send `recipientFax`: E.164-style (+country…).
 * NANP without country digit (10 digits) → prefix +1; already includes leading 1 (11 digits) → +digits only (no extra 1).
 * Other lengths → +digits (international).
 */
export function recipientFaxForSendApi(raw: string | null | undefined): string {
    const trimmed = (raw || '').trim();
    if (!trimmed) return '';
    const digits = trimmed.replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+${digits}`;
    }
    if (digits.length === 10) {
        return `+1${digits}`;
    }
    return `+${digits}`;
}

/**
 * Normalize a user-entered fax string to E.164-style (+digits) for send/contact APIs.
 * US 10-digit numbers get a +1 prefix; 11-digit strings starting with 1 become +1…
 */
export function normalizeFaxForApi(raw: string | null | undefined): string {
    const trimmed = (raw || '').trim();
    if (!trimmed) return '';
    const digits = trimmed.replace(/\D/g, '');
    if (!digits) return trimmed;
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+${digits}`;
    }
    if (digits.length === 10) {
        return `+1${digits}`;
    }
    return `+${digits}`;
}

/**
 * 10-digit NANP body for fax/phone form controls (e.g. USFaxNumberInput).
 * Strips non-digits; if 11 digits starting with country code 1, drops the leading 1.
 */
export function faxNanpDigitsForInput(raw: string | null | undefined): string {
    if (!raw) return '';
    let d = String(raw).replace(/\D/g, '');
    if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
    return d.slice(0, 10);
}

/** True when `id` is a server numeric contact id (not a local `contact_*` temp id). */
export function isFaxContactServerId(id: string): boolean {
    return /^\d+$/.test(id);
}

/** Map FaxContactVo from POST /fax/contact to dashboard FaxContact (single tag for table UI). */
export function mapFaxContactVoToFaxContact(vo: FaxContactVo): FaxContact {
    const tagStr = vo.tag?.length ? vo.tag[0] : undefined;
    return {
        id: String(vo.id),
        name: vo.name ?? '',
        faxNumber: vo.faxNumber ?? '',
        faxNumberDisplay: vo.faxNumberDisplay,
        notes: vo.notes,
        tag: tagStr,
        createAt: vo.createAt,
    };
}

/** R«Page«FaxContactVo»» from POST /fax/contact/list */
export function extractFaxContactListPage(body: unknown): {
    records: FaxContactVo[];
    total: number;
    current: number;
    size: number;
} {
    if (!isFaxScheduleSuccess(body)) {
        return { records: [], total: 0, current: 1, size: 20 };
    }
    const page = (body as { data?: TFaxContactListPage | null }).data;
    if (!page) {
        return { records: [], total: 0, current: 1, size: 20 };
    }
    return {
        records: page.records ?? [],
        total: page.total ?? 0,
        current: page.current ?? 1,
        size: page.size ?? 20,
    };
}

export function extractFaxPagedList(body: unknown): FaxMessageVo[] {
    return extractFaxPagedResponse(body).list;
}

export function extractFaxPagedResponse(body: unknown): {
    list: FaxMessageVo[];
    totalRecords: number;
    pageNo: number;
    pageSize: number;
} {
    if (!isFaxScheduleSuccess(body)) {
        return { list: [], totalRecords: 0, pageNo: 1, pageSize: 20 };
    }
    const page = (body as { data?: TFaxPagedMessages | null }).data;
    if (!page) {
        return { list: [], totalRecords: 0, pageNo: 1, pageSize: 20 };
    }
    return {
        list: page.list ?? [],
        totalRecords: page.totalRecords ?? 0,
        pageNo: page.pageNo ?? 1,
        pageSize: page.pageSize ?? 20,
    };
}

function tagToString(tag: FaxMessageVo['tag']): string | undefined {
    if (tag == null) return undefined;
    if (Array.isArray(tag)) {
        const s = tag.filter(Boolean).join(', ');
        return s || undefined;
    }
    return String(tag);
}

/** Inbound / received — table columns use faxCallerId, sender side. */
export function mapInboundFaxVo(vo: FaxMessageVo): EFaxListRow {
    return {
        id: vo.uuid || `fax-${vo.senderFax}-${vo.sentAt || vo.createAt}`,
        faxCallerId: vo.senderFax || '',
        faxNumber: vo.senderFax || '',
        /** Sender CSID / station ID from carrier (not the same as saved contact name). */
        faxCsid: vo.senderName,
        /** Linked contact from server, when present. */
        contactName: vo.contact?.name,
        subject: vo.subject,
        status: vo.status || '',
        tag: tagToString(vo.tag),
        aiSummary: vo.aiSummary,
        patient: vo.assignedClient?.name,
        patientId: vo.assignedClient?.clientId,
        createdAt: vo.sentAt || vo.createAt || '',
        isRead: vo.isRead,
        aiMatched: vo.patientMatchSource === 'AI',
    };
}

function normalizeSentStatus(raw: string | undefined): string {
    const s = (raw || '').toLowerCase();
    if (['processing', 'delivered', 'cancelled', 'failed'].includes(s)) return s;
    if (s.includes('deliver') || s === 'success') return 'delivered';
    if (s.includes('fail') || s.includes('error')) return 'failed';
    if (s.includes('cancel')) return 'cancelled';
    return s || 'processing';
}

/** Outbound / sent — table uses faxNumber as recipient. */
export function mapOutboundFaxVo(vo: FaxMessageVo): EFaxListRow {
    return {
        id: vo.uuid || `fax-${vo.recipientFax}-${vo.sentAt || vo.createAt}`,
        faxCallerId: '',
        faxNumber: vo.recipientFax || '',
        faxCsid: vo.recipientName || vo.contact?.name,
        subject: vo.subject,
        status: normalizeSentStatus(vo.status),
        tag: tagToString(vo.tag),
        aiSummary: vo.aiSummary,
        patient: vo.assignedClient?.name,
        patientId: vo.assignedClient?.clientId,
        createdAt: vo.sentAt || vo.createAt || '',
        isRead: vo.isRead,
        aiMatched: vo.patientMatchSource === 'AI',
    };
}

export function showReceivedUnreadDot(row: EFaxListRow): boolean {
    if (row.isRead !== undefined) return !row.isRead;
    return row.status !== 'success';
}
